import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {CommonConstant} from '../../../app.constants';
import {NewUser, UserProfile} from '../../../../models/user';
import {UserService} from '../../../services/user.service';
import {PlaceholderVisibilityService} from '../../../services/placeholder-visibility/placeholder-visibility.service';
import {TranslateService} from '@ngx-translate/core';
import {WebUtils} from '../../../utils/web.utils';
import {ActivatedRoute} from '@angular/router';
import {ModalService} from '../../../services/modal/modal.service';
import {AppSettings} from '../../../utils/app-settings';

@Component({
  selector: 'sign-up-form',
  templateUrl: './sign-up-form.component.html',
  styleUrls: ['./sign-up-form.component.scss']
})
export class SignUpFormComponent implements OnInit {
  public newUser: FormGroup;
  private _errorMessage: string;
  private hashKey: string;
  private planId: string;
  @Output() signUpSuccessCallback: EventEmitter<any> = new EventEmitter();
  passwordControl = new FormControl(undefined, {
    validators: this.validatePassword.bind(this),
    updateOn: 'blur'
  });

  confirmPasswordControl = new FormControl(undefined, {
    validators: this.validatePassword.bind(this),
    updateOn: 'blur'
  });

  public invitedCompany;
  private signUpErrorModalRef;

  constructor(private formBuilder: FormBuilder,
              private userService: UserService,
              private modalService: ModalService,
              private translate: TranslateService,
              private activatedRoute: ActivatedRoute,
              private placeholderService: PlaceholderVisibilityService) { }

  validatePassword() {
    if (!this.newUser) {
      return null;
    }
    const password = this.newUser.controls.password.value;
    const confirmPassword = this.newUser.controls.confirmPassword.value;
    if (!confirmPassword && !password) {
      return null;
    }

    if (confirmPassword !== password) {
      return {
        doesMatchPassword: false
      };
    }
    return null;
  }

  validateEmail(emailFormControl) {
    if (!emailFormControl) {
      return null;
    }
    if (!WebUtils.validateEmail(emailFormControl.value)) {
      return {
        invalidEmail: true
      };
    }
    return null;
  }

  onUpdateTermAndServiceAgreement() {
    this._errorMessage = undefined;
  }


  ngOnInit() {
    this.newUser = this.formBuilder.group({
      fullName: [ null,  Validators.required ],
      email: [ null, [Validators.required, this.validateEmail.bind(this)] ],
      password: this.passwordControl,
      confirmPassword: this.confirmPasswordControl,
      acceptTermService: [ null ]
    });

    this.confirmPasswordControl.valueChanges.subscribe((evt) => {
      if (evt) {
        this.passwordControl.updateValueAndValidity();
      }
    });

    this.activatedRoute.params.subscribe(res => {
      if (res.hashKey) {
        this.hashKey = res.hashKey;
        this.placeholderService.show(CommonConstant.LOGIN_PLACEHOLDER);
        this.userService.precheckHashLink(res.hashKey).subscribe((user) => {
          this.invitedCompany = user;
          this.newUser.patchValue({email: user.email});
          this.placeholderService.hide(CommonConstant.LOGIN_PLACEHOLDER);
        }, err => {
          this._errorMessage = err.error.message;
          this.placeholderService.hide(CommonConstant.LOGIN_PLACEHOLDER);
        });
      }
    });
    if (this.activatedRoute.snapshot.queryParamMap.has('plan_id')) {
      this.planId = this.activatedRoute.snapshot.queryParamMap.get('plan_id');
    }
  }

  onSubmit({ value, valid }: {value: NewUser, valid: boolean}) {
    const isAcceptTermService = value.acceptTermService;
    if (!isAcceptTermService) {
      this._errorMessage = this.translate.instant('register.error_term_service_missing');
      return;
    }
    if (valid && isAcceptTermService) {
      this.placeholderService.show(CommonConstant.LOGIN_PLACEHOLDER);
      if (this.invitedCompany) {
        const data = new UserProfile();
        data.fullName = value.fullName;
        data.email = value.email;
        data.password = value.password;
        data.confirmPassword = value.confirmPassword;
        data.country = CommonConstant.INTERNATIONAL_ID;
        this.userService.acceptionInvitation(this.hashKey, data, this.companyName).subscribe(res => {
          this.onSuccess(value, res);
        });
      } else {
        if (this.planId) {
          value.plan_id = this.planId;
        }
        this.userService.signUp(value).subscribe(res => {
          this.onSuccess(value, res);
        }, err => {
          this.onError(this.buildErrorMessage(err));
        });
      }
    } else {
      this.onError(this.translate.instant('register.error_term_service_missing'));
    }
    return false;
  }

  private buildErrorMessage(err) {
    let errorMsg;
    if (err && err.error && err.error.message === 'invalid_email') {
      errorMsg = this.translate.instant('register.error_email_invalid');
    } else if (err && err.error && err.error.data === 'user_invalid') {
      errorMsg = this.translate.instant('register.error_email_used');
    } else if (err && err.error && err.error.data === 'user_invalid') {
      errorMsg = this.translate.instant('register.invalid_plan');
    } else {
      errorMsg = this.translate.instant('register.unexpected_error');
    }
    return errorMsg;
  }

  private onSuccess(user, res) {
    this.signUpSuccessCallback.emit({
      invited: this.invitedCompany !== undefined,
      userData: {
        email: user.email,
        ...res
      }
    });
  }

  private onError(errorMsg) {
    this._errorMessage = errorMsg;
    this.placeholderService.hide(CommonConstant.LOGIN_PLACEHOLDER);
  }

  get errorMessage() {
    if (this._errorMessage && this._errorMessage !== '') {
      return this._errorMessage;
    }

    if (!this.newUser || !this.newUser.controls) {
      return null;
    }

    if (this.newUser.controls.email && this.newUser.controls.email.touched
      && this.newUser.controls.email.errors
      && this.newUser.controls.email.errors.invalidEmail === true) {
      return this.translate.instant('register.invalid_email_message');
    }

    if (this.newUser.controls.password
          && this.newUser.controls.password.touched
          && this.newUser.controls.password.errors
          && this.newUser.controls.password.errors.doesMatchPassword === false) {
      return this.translate.instant('register.error_password_not_match');
    }

    let hasErrors = false;
    Object.keys(this.newUser.controls).forEach(k => {
      if (this.newUser.controls[k].touched && this.newUser.controls[k].errors) {
        hasErrors = true;
      }
    });

    if (hasErrors) {
      return this.translate.instant('register.error_missing_required');
    } else {
      return null;
    }
  }

  get companyName() {
    return this.invitedCompany ? this.invitedCompany.tempCompanyName : '';
  }

  get accountId() {
    return this.invitedCompany ? this.invitedCompany.id : '';
  }

  openSupport() {
    if (typeof (<any>window).Intercom === 'function') {
      (<any>window).Intercom('showMessages');
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/auth/sign-up/sign-up-form/sign-up-form.component.ts